import { Injectable } from '@angular/core';
import { AuthenticationToken } from '@core/authentication/authentication-token';
import { AssetClassValueObject } from '@core/models/asset-class-value-object';
import { Company } from '@core/models/company.model';
import { NotificationsCounters } from '@core/models/notification.model';
import { Privileges } from '@core/models/privileges.model';
import { User } from '@core/models/user.model';
import { ValueObject } from '@core/models/value-object.model';
import { ListViewType } from '@core/views/list-view-type';
import { ViewType } from '@core/views/view-type';
import { deserialize, serialize } from '@junte/serialize-ts';

import { UserDetails } from '../models/user-details.model';
import { UserProfile } from '../models/user-profile.model';

import { UserOverview } from './../models/user-overview.model';
import { createInitialState, AppStore, DetailContext } from './app-state.store';

@Injectable({
  providedIn: 'root',
})
export class AppStoreService {
  constructor(private store: AppStore) {}

  public reset(): void {
    this.store.update((state) => ({
      ...createInitialState(),
      listViewType: state.listViewType,
    }));
  }

  public updateAuthentication(tokens: AuthenticationToken): void {
    this.store.update((state) => ({
      ...state,
      authentication: tokens,
    }));
  }

  public updateAvailableAssetClasses(availableAssetClasses: AssetClassValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableAssetClasses,
    }));
  }

  public updateAvailableCompanies(availableCompanies: Company[]): void {
    this.store.update((state) => ({
      ...state,
      availableCompanies,
    }));
  }

  public updateAvailableCountries(availableCountries: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableCountries,
    }));
  }

  public updateAvailableEsgComplianceOptions(availableEsgComplianceOptions: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableEsgComplianceOptions,
    }));
  }

  public updateAvailableIndustries(availableIndustries: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableIndustries,
    }));
  }

  public updateAvailableInvestorClasses(availableInvestorClasses: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableInvestorClasses,
    }));
  }

  public updateAvailableClientClasses(availableClientClasses: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableClientClasses,
    }));
  }

  public updateAvailableLanguages(availableLanguages: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableLanguages,
    }));
  }

  public updateAvailableRatings(availableRatings: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableRatings,
    }));
  }

  public updateAvailableSalutations(availableSalutations: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableSalutations,
    }));
  }

  public updateAvailableTitles(availableTitles: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableTitles,
    }));
  }

  public updateAvailableOccupations(availableOccupations: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableOccupations,
    }));
  }

  public updateAvailableBusinessTypes(availableBusinessTypes: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableBusinessTypes,
    }));
  }
  public updateAvailableMainSourceOfFunds(availableMainSourceOfFunds: ValueObject[]): void {
    this.store.update((state) => ({
      ...state,
      availableMainSourceOfFunds,
    }));
  }

  public updateCurrentDetailContext(currentDetailContext: DetailContext): void {
    this.store.update((state) => ({
      ...state,
      currentDetailContext,
    }));
  }

  public updateCurrentReturnUrl(currentReturnUrl: string): void {
    this.store.update((state) => ({
      ...state,
      currentReturnUrl,
    }));
  }

  public updateIsPartnerScreen(isPartnerScreen: boolean): void {
    this.store.update((state) => ({
      ...state,
      isPartnerScreen,
    }));
  }

  public updateCurrentRoute(currentRoute: string[]): void {
    this.store.update((state) => ({
      ...state,
      currentRoute,
    }));
  }

  public updateNotificationsCounters(notificationsCounters: NotificationsCounters): void {
    this.store.update((state) => ({
      ...state,
      notificationsCounters,
    }));
  }

  public updateUser(user: User): void {
    this.store.update((state) => ({
      ...state,
      user,
    }));
  }

  public updateUserProfile(userProfile: UserProfile): void {
    this.store.update((state) => ({
      ...state,
      userProfile,
    }));

    // WAI-577: Update this part when the User and UserProfile are merged
    if (userProfile) {
      const user = serialize<User>(userProfile);
      this.updateUser(deserialize({ ...user }, User));
    }
  }

  public updateUserDetails(item: UserDetails): void {
    this.store.update((state) => ({
      ...state,
      userDetails: item,
    }));
  }

  public updateUsersOverview(usersOverview: UserOverview[]): void {
    this.store.update((state) => ({
      ...state,
      usersOverview,
    }));
  }

  public updatePrivileges(privileges: Privileges): void {
    this.store.update((state) => ({
      ...state,
      privileges,
    }));
  }

  public updateProductListViewType(listViewType: ListViewType): void {
    this.store.update((state) => ({
      ...state,
      listViewType,
    }));
  }

  public updateViewType(viewType: ViewType): void {
    this.store.update((state) => ({
      ...state,
      viewType,
    }));
  }

  public updateCurrentProductNdaRequirement(currentProductNdaRequirement: boolean): void {
    this.store.update((state) => ({
      ...state,
      currentProductNdaRequirement,
    }));
  }
}
